import { Component, HostBinding, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { LoaderService } from '../../../shared/services';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { ZoningCodeService } from '../../core/services';
import { OverlayType } from '../../../shared/enums/overlay-type.enum';
import { OverlayModel } from '../../models';
import IOverlay = ZoneIQCalibration.IOverlay;
import { OverlayTypology } from '../../../shared/enums/overlay-typology.enum';
import { CalibrationSetType } from '../../../shared/enums';
import { OverlayTypologyService } from '../../core/services/overlay-typology.service';
import { IOverlayTypologyModel } from 'src/app/interfaces/overlay-typology-model.interface';
import { IZoningCodeModel } from 'src/app/interfaces/zoning-code-model.interface';
import { IOverlayModel } from 'src/app/interfaces/overlay-model.interface';

interface ITypologyData {
    Name: string;
    Id: number;
}

@Component({
    standalone: false,
    selector: 'overlay-typology',
    templateUrl: './overlay-typology.component.html',
    styleUrls: ['./overlay-typology.component.scss'],
    encapsulation: ViewEncapsulation.None

})
export class OverlayTypologyComponent implements OnInit, OnDestroy {
    @Input() currentTab: string;
    @HostBinding('class')
    readonly hostClass = 'ziq-horizontal-scroll-table';
    subscription = new Subscription();
    zoningCode: IZoningCodeModel = null;
    typologiesArr: IOverlayModel[] = [];
    private overlaysArr: IOverlayModel[] = [];
    private typologyDependencies = {
        [OverlayTypology.ADD_TO_LIST]: [OverlayTypology.ADD_TO_LIST, OverlayTypology.DEFAULT],
        [OverlayTypology.ENABLE]: [OverlayTypology.ENABLE, OverlayTypology.ADD_TO_LIST, OverlayTypology.DEFAULT]
    };

    private _identifiers: { [property: string]: any } = {};

    get overlays() {
        if (!this.zoningCode) {
            return;
        }
        return this.overlaysArr;
    }

    get typologies() {
        if (!this.zoningCode) {
            return;
        }
        return this.typologiesArr;
    }

    constructor(
        private zcService: ZoningCodeService,
        public overlayTypologyService: OverlayTypologyService,
        private loaderService: LoaderService
    ) {}

    ngOnInit() {
        this.loaderService.show();
        this.subscription.add(
            this.zcService.zoningCodes.pipe(filter(zc => zc !== null && zc.length > 0)).subscribe(zc => {
                this.zoningCode = zc[0];
                this.zoningCode.Overlays.forEach(overlay => {
                    if (overlay.BuildingCalibrationType !== OverlayType.BUILDING_TYPOLOGY) {
                        this.overlaysArr.push(overlay);
                    } else {
                        // this.typologiesArr.push({ Name: overlay.Name, Id: overlay.Id });
                        this.typologiesArr.push(overlay);
                    }
                });
                this.typologiesArr.sort(OverlayModel.sortingCalibrationByName);
                this.overlaysArr.sort(OverlayModel.sortingCalibrationByName);
                this.loaderService.hide();
            })
        );
    }

    isApplicableTypology(overlayIndex: number, typologyIndex: number) {
        return this.overlays[overlayIndex].OverlayTypology[typologyIndex].currentState.IsEnabled !== null;
    }

    setDefault(value: boolean, indexOverlay: number, indexTypology: number) {
        const overlay = this.overlays[indexOverlay];
        if (value) {
            const elem = overlay.OverlayTypology.find(overType => overType.currentState.IsDefault);
            if (elem) {
                elem.currentState.IsDefault = false;
            }
        }
        this.setValue(overlay.OverlayTypology[indexTypology], value, 'IsDefault');
    }

    getIdentifier(typology, type: number) {
        const key = `${typology.Id}-${type}`;
        if (!this._identifiers[key]) {
            this._identifiers[key] = {
                calibrationSetId: typology.Id,
                calibrationSetType: type,
                typology: typology.Id === null ? typology : null
            };
        }
        return this._identifiers[key];
    }

    getOverlayIdentifier(overlay: IOverlay) {
        return this.getIdentifier(overlay, CalibrationSetType.OVERLAY);
    }

    getTypologyIdentifier(typology: ITypologyData) {
        return this.getIdentifier(typology, CalibrationSetType.TYPOLOGY);
    }

    getOverlayTypologyIdentifier(overlayTypology: ITypologyData) {
        return this.getIdentifier(overlayTypology, CalibrationSetType.OVERLAY_TYPOLOGY);
    }

    getBeforeSave(Id: number) {
        return Id ? null : this.beforeSave.bind(this);
    }

    beforeSave(data) {
        const parameters = data.parameters;
        const typology = JSON.parse(JSON.stringify(parameters.Typology));
        delete typology.Name;
        return this.overlayTypologyService.saveChanges([typology]);
    }

    setValue(typology: IOverlayTypologyModel, value: boolean, property: string) {
        let propertyList;

        if (!value && property === OverlayTypology.ENABLE) {
            propertyList = this.typologyDependencies[OverlayTypology.ENABLE];
        }
        if (!value && property === OverlayTypology.ADD_TO_LIST) {
            propertyList = this.typologyDependencies[OverlayTypology.ADD_TO_LIST];
        }
        if (value || property === OverlayTypology.DEFAULT) {
            propertyList = [property];
        }

        propertyList.forEach(prop => (typology.currentState[prop] = value));
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }
}
